/**
 * Resolve o modeloRequerimento/tiposAssunto/tipoPedido/comissaoRequerida de um
 * pedido a partir do seu `protocoloConfig` (config.js), do formData atual e dos
 * dados de OAB do usuário logado. Função pura — nenhuma chamada de API aqui.
 *
 * `protocoloConfig` tem 2 formatos possíveis:
 *  - { modeloRequerimento, tiposAssunto, tipoPedido? } — tiposAssunto sempre fixo;
 *    modeloRequerimento e tipoPedido podem cada um ser uma string fixa OU um
 *    condicional { field | source, map } resolvido contra formData (campo do
 *    formulário) ou oabData (categoria OAB: Advogado/Estagiário; com
 *    `fieldSemCategoria`, quem não tem categoria usa esse campo) — independentes
 *    entre si (ex: Alteração de Nome tem modeloRequerimento condicional mas
 *    tipoPedido fixo).
 *  - { field | source, map } (sem tiposAssunto no nível raiz) — usado só quando
 *    modeloRequerimento, tiposAssunto E tipoPedido variam juntos pela mesma opção
 *    (ex: Distrato de Sociedade, cujo tiposAssunto também muda entre
 *    Unipessoal/Pluripessoal); nesse caso cada valor do `map` já é o trio completo
 *    { modeloRequerimento, tiposAssunto, tipoPedido }.
 *
 * `comissaoRequerida` só existe pra pedidos que declaram `comissaoRequeridaField`
 * (hoje só Inscrições para Comissões OAB/MS, tipo Advogado) — resolvido a partir
 * do campo multiSelect do formulário, usando a 1ª comissão escolhida.
 */

const resolveCondicional = (condicional, formData, oabData) => {
  let valor;

  if (condicional.field) {
    valor = formData?.[condicional.field];
  } else if (condicional.source === 'oabCategoria') {
    // Sem categoria OAB (PF não inscrita), vale a resposta do formulário, se o
    // pedido declarar `fieldSemCategoria` (ex: Juntadas, ver juntadaSections).
    valor = oabData?.tipoCategoria
      || (condicional.fieldSemCategoria ? formData?.[condicional.fieldSemCategoria] : undefined);
  } else {
    throw new Error('protocoloConfig em formato condicional não suportado (esperado "field" ou "source")');
  }

  const resolvido = condicional.map[valor];
  if (resolvido === undefined) {
    throw new Error(`Não foi possível resolver o protocolo deste pedido (valor "${valor}" não mapeado)`);
  }
  return resolvido;
};

// Resolve um valor de protocoloConfig que pode ser fixo (string) ou condicional
// ({ field | source, map }) — usado tanto pra modeloRequerimento quanto pra tipoPedido.
const resolveValor = (valor, formData, oabData) => {
  if (valor === undefined) return undefined;
  return typeof valor === 'string' ? valor : resolveCondicional(valor, formData, oabData);
};

// comissaoRequerida vem de um multiSelect (a pessoa pode marcar várias comissões
// de interesse) mas a API só aceita 1 ID — manda a primeira escolhida.
const resolveComissaoRequerida = (comissaoRequeridaField, formData) => {
  if (!comissaoRequeridaField) return undefined;
  const valor = formData?.[comissaoRequeridaField];
  return Array.isArray(valor) ? valor[0] : valor;
};

export const resolveProtocoloDados = (protocoloConfig, formData = {}, oabData = null) => {
  if (!protocoloConfig) {
    throw new Error('Pedido sem protocoloConfig configurado');
  }

  // modeloRequerimento, tiposAssunto e tipoPedido variam juntos pela mesma opção
  if (protocoloConfig.tiposAssunto === undefined) {
    const { modeloRequerimento, tiposAssunto, tipoPedido } = resolveCondicional(protocoloConfig, formData, oabData);
    return { modeloRequerimento, tiposAssunto, tipoPedido, comissaoRequerida: undefined };
  }

  const { tiposAssunto, modeloRequerimento, tipoPedido, comissaoRequeridaField } = protocoloConfig;

  return {
    modeloRequerimento: resolveValor(modeloRequerimento, formData, oabData),
    tiposAssunto,
    tipoPedido: resolveValor(tipoPedido, formData, oabData),
    comissaoRequerida: resolveComissaoRequerida(comissaoRequeridaField, formData)
  };
};
